import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams, useParams } from 'react-router-dom';
import { AvailabilitySlot } from '../../types/appointment.types';
import { Barber, BarberOfferedService } from '../../types/barber.types';
import { Branch } from '../../types/branch.types';
import { barberService } from '../../services/barberService';
import { branchService } from '../../services/branchService';
import { appointmentService } from '../../services/appointmentService';
import { promotionService } from '../../services/promotionService';
import { Promotion } from '../../types/promotion.types';
import { useToast } from '../../context/ToastContext';
import { generateBarberSlug } from '../../utils/slugUtils';
import { getLocalDateString } from '../../utils/dateUtils';

// Styles
import '../../components/common/Wizard.css';
import './AppointmentBooking.css';

const AppointmentBooking: React.FC = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { slug } = useParams<{ slug?: string }>();
    const { showToast } = useToast();
    const [step, setStep] = useState(1);

    // Data
    const [branches, setBranches] = useState<Branch[]>([]);
    const [barbers, setBarbers] = useState<Barber[]>([]);
    const [slots, setSlots] = useState<AvailabilitySlot[]>([]);
    const [activePromotions, setActivePromotions] = useState<Promotion[]>([]);

    // Selection
    const [selectedBranch, setSelectedBranch] = useState<Branch | null>(null);
    const [selectedBarber, setSelectedBarber] = useState<Barber | null>(null);
    const [selectedDate, setSelectedDate] = useState<string>('');
    const [selectedTime, setSelectedTime] = useState<string>('');
    const [selectedService, setSelectedService] = useState<BarberOfferedService | null>(null);
    const [selectedPromotion, setSelectedPromotion] = useState<Promotion | null>(null);
    const [notes, setNotes] = useState<string>('');

    // Week navigation state
    const [weekOffset, setWeekOffset] = useState(0);

    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [initialLoading, setInitialLoading] = useState(true);

    useEffect(() => {
        loadInitialData();
    }, []);

    const loadInitialData = async () => {
        try {
            const [branchesData, promosData] = await Promise.all([
                branchService.getAllBranches(),
                promotionService.getActivePromotions().catch(() => []) // non-blocking error for promos
            ]);
            setBranches(branchesData);
            setActivePromotions(promosData);

            const targetBarberId = searchParams.get('barberId');

            if (slug || targetBarberId) {
                try {
                    let barber = null;
                    if (slug) {
                        const allBarbers = await barberService.getAllBarbers();
                        const basicBarber = allBarbers.find(b => generateBarberSlug(b) === slug);
                        if (basicBarber) {
                            barber = await barberService.getBarberById(basicBarber.id);
                        }
                    } else if (targetBarberId) {
                        barber = await barberService.getBarberById(Number(targetBarberId));
                    }
                    
                    if (barber) {
                        // Find the branch this barber works at (from schedules)
                        const branchId = barber.schedules && barber.schedules.length > 0
                            ? barber.schedules[0].branchId
                            : branchesData[0]?.id;
                        
                        const branch = branchesData.find(b => b.id === branchId);
                        if (branch) {
                            setSelectedBranch(branch);
                            setSelectedBarber(barber);
                            if (barber.services && barber.services.length > 0) {
                                setSelectedService(barber.services[0]);
                            }
                            setStep(3);
                            return; // Skip normal initialization
                        }
                    } else if (slug) {
                        showToast('No se encontró al barbero con ese enlace', 'error');
                    }
                } catch (err) {
                    console.error('Error pre-loading barber:', err);
                    showToast('No se pudo cargar el barbero especificado', 'error');
                }
            }

            if (branchesData.length === 1) {
                // If only one branch, select it and skip to step 2
                const singleBranch = branchesData[0];
                setSelectedBranch(singleBranch);

                // Load barbers for this branch immediately
                const barbersData = await barberService.getBarbersByBranch(singleBranch.id);
                setBarbers(barbersData);
                setStep(2);
            }
        } catch (error) {
            console.error('Error loading initial data:', error);
            showToast('Error al cargar datos', 'error');
        } finally {
            setInitialLoading(false);
        }
    };

    const handleBranchSelect = async (branch: Branch) => {
        setSelectedBranch(branch);
        setSelectedBarber(null); // Reset barber
        setLoading(true);
        try {
            const data = await barberService.getBarbersByBranch(branch.id);
            setBarbers(data);
            setStep(2);
        } catch (error) {
            console.error('Error loading barbers:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleBarberSelect = (barber: Barber) => {
        setSelectedBarber(barber);
        setSelectedPromotion(null);
        // Pre-select first service if available
        if (barber.services && barber.services.length > 0) {
            setSelectedService(barber.services[0]);
        }
        setStep(3);
    };

    const handleDateChange = async (date: string) => {
        setSelectedDate(date);
        setSelectedTime(''); // Reset time
        if (selectedBarber && date) {
            setLoading(true);
            try {
                const consecutiveSlots = selectedPromotion ? selectedPromotion.multiplier : 1;
                const availableSlots = await appointmentService.getAvailableSlots(selectedBarber.id, date, consecutiveSlots);
                setSlots(availableSlots);
            } catch (error) {
                console.error('Error loading slots:', error);
            } finally {
                setLoading(false);
            }
        }
    };

    const handleServiceSelect = (service: BarberOfferedService) => {
        setSelectedService(service);
        setSelectedPromotion(null);
        setSelectedTime('');
        if (selectedDate && selectedBarber) {
            appointmentService.getAvailableSlots(selectedBarber.id, selectedDate, 1).then(setSlots);
        }
    };

    const handlePromotionSelect = (promo: Promotion) => {
        setSelectedPromotion(promo);
        setSelectedService(null);
        setSelectedTime('');
        if (selectedDate && selectedBarber) {
            appointmentService.getAvailableSlots(selectedBarber.id, selectedDate, promo.multiplier).then(setSlots);
        }
    };

    const handleConfirmBooking = async () => {
        if (!selectedBarber || !selectedBranch || !selectedDate || !selectedTime) return;
        if (!selectedService && !selectedPromotion) return;

        setSubmitting(true);
        try {
            if (selectedPromotion) {
                await appointmentService.createBundleAppointment({
                    barberId: selectedBarber.id,
                    branchId: selectedBranch.id,
                    appointmentDate: selectedDate,
                    startTime: selectedTime,
                    promotionId: selectedPromotion.id,
                    bundleName: selectedPromotion.name,
                    totalPrice: selectedPromotion.price,
                    numberOfSlots: selectedPromotion.multiplier,
                    notes: notes
                });
            } else if (selectedService) {
                await appointmentService.createAppointment({
                    barberId: selectedBarber.id,
                    branchId: selectedBranch.id,
                    appointmentDate: selectedDate,
                    startTime: selectedTime,
                    barberServiceId: selectedService.id,
                    serviceDescription: selectedService.name,
                    notes: notes
                });
            }
            navigate('/client/dashboard');
        } catch (error) {
            console.error('Error creating appointment:', error);
            showToast('Error al crear el turno. Por favor intente nuevamente.', 'error');
        } finally {
            setSubmitting(false);
        }
    };

    const formatPrice = (price?: number) => {
        if (price === undefined) return '$0';
        return new Intl.NumberFormat('es-AR', {
            style: 'currency',
            currency: 'ARS'
        }).format(price);
    };

    // Week generation logic
    const weekDays = useMemo(() => {
        const start = new Date();
        // Adjust to the Monday of the current relative week
        const day = start.getDay();
        const diff = start.getDate() - day + (day === 0 ? -6 : 1) + (weekOffset * 7);
        start.setDate(diff);

        const days = [];
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        for (let i = 0; i < 7; i++) {
            const date = new Date(start);
            date.setDate(start.getDate() + i);
            date.setHours(0, 0, 0, 0);

            const dateStr = getLocalDateString(date);
            const isPast = date < today;

            days.push({
                dateStr,
                dayName: date.toLocaleDateString('es-ES', { weekday: 'short' }),
                dayNumber: date.getDate(),
                month: date.toLocaleDateString('es-ES', { month: 'short' }),
                disabled: isPast
            });
        }
        return days;
    }, [weekOffset]);

    const availablePromos = useMemo(() => {
        return activePromotions.filter(promo => 
            selectedBarber && 
            selectedBarber.isIndependent !== true && 
            selectedBarber.services?.some(s => s.serviceTypeId === promo.serviceTypeId)
        );
    }, [activePromotions, selectedBarber]);

    if (initialLoading) {
        return (
            <div className="container mt-lg flex flex-col items-center justify-center" style={{ minHeight: '50vh' }}>
                <div className="spinner mb-md"></div>
                <p className="text-secondary">Cargando opciones...</p>
            </div>
        );
    }

    return (
        <div className="container mt-lg fade-in" style={{ maxWidth: '800px' }}>
            <div className="flex items-center gap-sm mb-lg">
                <button className="btn btn-outline btn-sm" onClick={() => navigate('/client/dashboard')}>&larr; Volver</button>
                <h1 className="m-0">Nuevo Turno</h1>
            </div>

            {/* Progress Bar */}
            <div className="flex justify-between items-center mb-xl relative" style={{ padding: '0 10%' }}>
                <div className="absolute top-1/2 left-0 w-full h-1 bg-gray-200 -z-10"></div>
                {[1, 2, 3, 4].map((s) => (
                    <div
                        key={s}
                        className={`w-10 h-10 rounded-full flex items-center justify-center font-bold transition-all shadow-sm ${step >= s ? 'bg-primary text-white scale-110' : 'bg-gray-200 text-gray-500'}`}
                    >
                        {s}
                    </div>
                ))}
            </div>

            {/* Step 1: Select Branch */}
            {step === 1 && (
                <div className="fade-in">
                    <h2 className="mb-md">1. Elige una Sucursal</h2>
                    {branches.length === 0 ? (
                        <p>Cargando sucursales...</p>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-md">
                            {branches.map(branch => (
                                <div
                                    key={branch.id}
                                    className={`card cursor-pointer hover:shadow-lg transition-all border-2 ${selectedBranch?.id === branch.id ? 'border-accent bg-accent-light bg-opacity-5' : 'border-transparent'}`}
                                    onClick={() => handleBranchSelect(branch)}
                                >
                                    <h3 className="m-0 font-bold">{branch.name}</h3>
                                    <p className="text-sm text-secondary">{branch.address}, {branch.city}</p>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {/* Step 2: Select Barber */}
            {step === 2 && (
                <div className="fade-in">
                    <div className="flex justify-between items-center mb-md">
                        <h2 className="m-0">2. Elige un Barbero</h2>
                        {branches.length > 1 && (
                            <button className="btn btn-sm btn-outline" onClick={() => setStep(1)}>Cambiar Sucursal</button>
                        )}
                    </div>

                    {loading ? (
                        <div className="spinner"></div>
                    ) : barbers.length === 0 ? (
                        <div className="card p-lg text-center">
                            <p>No hay barberos disponibles en esta sucursal.</p>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-md">
                            {barbers.map(barber => (
                                <div
                                    key={barber.id}
                                    className={`card cursor-pointer hover:shadow-lg transition-all border-2 flex items-center gap-md ${selectedBarber?.id === barber.id ? 'border-accent bg-accent-light bg-opacity-5' : 'border-transparent'}`}
                                    onClick={() => handleBarberSelect(barber)}
                                >
                                    <div className="bg-gray-200 rounded-full w-12 h-12 flex items-center justify-center font-bold text-xl text-gray-600">
                                        {barber.firstName?.charAt(0)}{barber.lastName?.charAt(0)}
                                    </div>
                                    <div>
                                        <h3 className="m-0 font-bold">{barber.firstName} {barber.lastName}</h3>
                                        <p className="text-sm text-secondary">{barber.specialization}</p>
                                        <div className="flex gap-xs flex-wrap mt-xs">
                                            {barber.services?.slice(0, 2).map((s, i) => (
                                                <span key={i} className="badge badge-info" style={{ fontSize: '0.7rem' }}>{s.name}</span>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {/* Step 3: Select Date, Time & Service */}
            {step === 3 && (
                <div className="fade-in">
                    <div className="flex justify-between items-center mb-lg">
                        <h2 className="m-0">3. Reservar con {selectedBarber?.nickname || selectedBarber?.firstName}</h2>
                        <button className="btn btn-sm btn-outline" onClick={() => setStep(2)}>Cambiar Barbero</button>
                    </div>

                    <div className="mb-xl">
                        <label className="form-label mb-md">Elige un Servicio</label>
                        <div className="selection-grid">
                            {selectedBarber?.services?.map(service => (
                                <div
                                    key={service.id}
                                    className={`selector-card ${selectedService?.id === service.id ? 'selected' : ''}`}
                                    onClick={() => handleServiceSelect(service)}
                                >
                                    <h4>{service.name}</h4>
                                    <div className="price">{formatPrice(service.price)}</div>
                                    <div className="duration">{service.durationMinutes} min</div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {availablePromos.length > 0 && (
                        <div className="mb-xl">
                            <label className="form-label mb-md text-accent flex items-center gap-xs">
                                <span>🎁</span> Promociones Especiales
                            </label>
                            <div className="selection-grid">
                                {availablePromos.map(promo => (
                                    <div
                                        key={promo.id}
                                        className={`selector-card ${selectedPromotion?.id === promo.id ? 'selected bg-accent-light bg-opacity-10 border-accent' : ''}`}
                                        onClick={() => handlePromotionSelect(promo)}
                                        style={selectedPromotion?.id === promo.id ? { borderColor: 'var(--accent)' } : {}}
                                    >
                                        <h4 className="text-accent">{promo.name}</h4>
                                        <div className="price">{formatPrice(promo.price)}</div>
                                        <div className="duration">{promo.serviceTypeName} (x{promo.multiplier})</div>
                                        {promo.description && <div className="text-xs text-secondary mt-xs">{promo.description}</div>}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    <div className="mb-xl">
                        <label className="form-label mb-md">Elige una Fecha</label>
                        <div className="week-selector-container">
                            <button
                                className="nav-btn"
                                onClick={() => setWeekOffset(prev => prev - 1)}
                                disabled={weekOffset <= 0}
                            >
                                &larr;
                            </button>

                            <div className="week-selector-scroll">
                                {weekDays.map((day, idx) => (
                                    <div
                                        key={idx}
                                        className={`date-pill ${selectedDate === day.dateStr ? 'selected' : ''} ${day.disabled ? 'disabled' : ''}`}
                                        onClick={() => !day.disabled && handleDateChange(day.dateStr)}
                                    >
                                        <span className="day-name">{day.dayName}</span>
                                        <span className="day-number">{day.dayNumber}</span>
                                        <span className="month">{day.month}</span>
                                    </div>
                                ))}
                            </div>

                            <button
                                className="nav-btn"
                                onClick={() => setWeekOffset(prev => prev + 1)}
                            >
                                &rarr;
                            </button>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-lg mb-xl">
                        <div>
                            <label className="form-label">Horarios Disponibles</label>
                            {!selectedDate ? (
                                <p className="text-secondary text-sm card p-md bg-gray-50 border-dashed border-2">Selecciona una fecha primero para ver los horarios.</p>
                            ) : loading ? (
                                <div className="spinner" style={{ margin: '2rem auto' }}></div>
                            ) : slots.length === 0 ? (
                                <p className="text-error card p-md bg-error-light border-2 border-error border-opacity-20 text-center">No hay horarios disponibles para esta fecha.</p>
                            ) : (
                                <div className="grid grid-cols-3 gap-sm h-64 overflow-y-auto content-start p-sm bg-white rounded-lg border-2">
                                    {slots.map((slot, index) => (
                                        <button
                                            key={index}
                                            className={`btn btn-sm ${!slot.available ? 'opacity-30 cursor-not-allowed bg-gray-100' : selectedTime === slot.time ? 'btn-primary' : 'btn-outline'}`}
                                            disabled={!slot.available}
                                            onClick={() => setSelectedTime(slot.time)}
                                        >
                                            {slot.time}
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>

                        <div>
                            <label className="form-label">Notas para el Barbero</label>
                            <textarea
                                className="form-textarea"
                                value={notes}
                                onChange={(e) => setNotes(e.target.value)}
                                placeholder="Ej: Busco un degrade, barba, etc. (Opcional)"
                                rows={8}
                            />
                        </div>
                    </div>

                    <div className="flex justify-end mt-lg pt-md border-t">
                        <button
                            className="btn btn-primary btn-lg"
                            style={{ minWidth: '200px' }}
                            disabled={!selectedTime || (!selectedService && !selectedPromotion) || !selectedDate}
                            onClick={() => setStep(4)}
                        >
                            Continuar &rarr;
                        </button>
                    </div>
                </div>
            )}

            {/* Step 4: Confirm */}
            {step === 4 && (
                <div className="fade-in">
                    <h2 className="mb-md">4. Confirmar Turno</h2>
                    <div className="card mb-lg shadow-sm border-2 border-accent border-opacity-20">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-lg p-md">
                            <div className="flex flex-col gap-md">
                                <div>
                                    <p className="text-secondary text-xs uppercase font-bold">Barbero</p>
                                    <p className="font-bold text-lg">{selectedBarber?.firstName} {selectedBarber?.lastName}</p>
                                </div>
                                <div>
                                    <p className="text-secondary text-xs uppercase font-bold">Sucursal</p>
                                    <p className="font-bold">{selectedBranch?.name}</p>
                                    <p className="text-sm text-secondary">{selectedBranch?.address}</p>
                                </div>
                            </div>
                            <div className="flex flex-col gap-md">
                                <div>
                                    <p className="text-secondary text-xs uppercase font-bold">Fecha y Hora</p>
                                    <div className="pill-selection">
                                        <span className="status-pill info">
                                            📅 {new Date(selectedDate + 'T00:00:00').toLocaleDateString('es-ES', { day: 'numeric', month: 'long' })}
                                        </span>
                                        <span className="status-pill warning">
                                            ⏰ {selectedTime} hs
                                        </span>
                                    </div>
                                </div>
                                <div>
                                    <p className="text-secondary text-xs uppercase font-bold">Servicio / Promoción</p>
                                    {selectedPromotion ? (
                                        <div className="flex justify-between items-center bg-accent-light bg-opacity-10 p-sm rounded-lg border border-accent border-opacity-30">
                                            <div>
                                                <p className="font-bold text-accent">{selectedPromotion.name}</p>
                                                <p className="text-xs text-secondary">{selectedPromotion.serviceTypeName} (x{selectedPromotion.multiplier})</p>
                                            </div>
                                            <p className="font-bold text-accent text-xl">{formatPrice(selectedPromotion.price)}</p>
                                        </div>
                                    ) : (
                                        <div className="flex justify-between items-center bg-gray-50 p-sm rounded-lg">
                                            <div>
                                                <p className="font-bold">{selectedService?.name}</p>
                                                <p className="text-xs text-secondary">{selectedService?.durationMinutes} min</p>
                                            </div>
                                            <p className="font-bold text-accent text-xl">{formatPrice(selectedService?.price)}</p>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                        {notes && (
                            <div className="mt-md p-md bg-gray-50 border-t border-gray-100 italic text-sm">
                                <p className="text-secondary text-xs uppercase font-bold mb-xs not-italic">Notas</p>
                                "{notes}"
                            </div>
                        )}
                    </div>

                    <div className="flex justify-between gap-sm items-center">
                        <button className="btn btn-outline" onClick={() => setStep(3)} disabled={submitting}>
                            &larr; Revisar Datos
                        </button>
                        <button className="btn btn-primary btn-lg" style={{ minWidth: '220px' }} onClick={handleConfirmBooking} disabled={submitting}>
                            {submitting ? 'Reservando...' : 'Confirmar y Reservar'}
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AppointmentBooking;

